import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CreationDateObject, DeliveryNoteDocument, UploadDocumentResponse } from 'src/app/models/delivery-note/delivery-note.model';
import { DeliveryNoteDocumentType } from 'src/app/enum/delivery-note/delivery-note-document-type.enum';
import { formatCompleteStringDatetoCompleteDateFormated } from 'src/app/utils';

@Injectable({
  providedIn: 'root'
})
export class DeliveryNoteService {
  private readonly url = environment.url;

  private customHeaders: HttpHeaders = new HttpHeaders({
    'Cafler-User-Agent': 'CaflerLogisticsPlatform/1.0',
  });

  constructor(private http: HttpClient) { }
  uploadFileOrLink(
    isTempFile: boolean,
    name: string,
    linkUrl?: string,
    file?: File
  ): Observable<UploadDocumentResponse> {
    const url = `${this.url}/attached-file`;
    let params = new HttpParams()
      .set('isTempFile', isTempFile.toString())
      .set('pdfName', name);

    if (linkUrl) {
      params = params.set('pdfUrl', linkUrl);
      return this.http.post<UploadDocumentResponse>(url, undefined, {
        params,
        headers: this.customHeaders,
      }).pipe(
        catchError(error => this.handleError(error))
      );
    } else {
      const formData = new FormData();
      formData.append('file', file!);
      return this.http.post<UploadDocumentResponse>(url, formData, {
        params,
        headers: this.customHeaders,
      }).pipe(
        catchError(error => this.handleError(error))
      );
    }
  }
  getAttachedFilesAndLinks(): Observable<UploadDocumentResponse[]> {
    return this.http.get<UploadDocumentResponse[]>(
      `${this.url}/attached-file/all`,
      {
        headers: this.customHeaders,
      }
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  changeFileName(pdfId: string, newName: string): Observable<{ success: boolean }> {
    const params = new HttpParams()
      .set('pdfId', pdfId)
      .set('newAttachedFileName', newName);

    return this.http.patch<{ success: boolean }>(
      `${this.url}/attached-file/change-name`,
      undefined,
      {
        headers: this.customHeaders,
        params,
      }
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }
  deleteFileOrLink(pdfId: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${this.url}/attached-file/delete/${pdfId}`,
      {
        headers: this.customHeaders,
      
      }
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }
  downloadFile(fileId: string): Observable<Blob> {
    return this.http.get(
      `${this.url}/attached-file/download/${fileId}`,
      {
        responseType: 'blob',
        headers: this.customHeaders,
      }
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }
  triggerDownload(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  formatCreationDate(creationDate?: string | CreationDateObject): string | undefined {
    if (!creationDate) return undefined;
    const raw = typeof creationDate === 'string'
      ? creationDate
      : creationDate.completeDate;
    if (!raw) return undefined;
    const formatted = formatCompleteStringDatetoCompleteDateFormated(raw);
    return formatted || undefined;
  }

  private handleError(error: HttpErrorResponse | unknown): Observable<never> {
    console.error('Error in DeliveryNoteService:', error);
    return throwError(() => error);
  }
  mapToDeliveryNoteDocument(doc: UploadDocumentResponse): DeliveryNoteDocument {
    return {
      documentId: doc.Id,
      type: doc.PdfUrl ? DeliveryNoteDocumentType.Link : DeliveryNoteDocumentType.Document,
      name: doc.PdfName,
      link: doc.PdfUrl,
      fileSize: doc.Size ? (doc.Size).toFixed(2) + ' MB' : undefined,
      uploadedDate: this.formatCreationDate(doc.CreationDate),
      checked: false,
      isNew: false
    };
  }
}
